import { CheckCircle, Copy, XCircle } from '@phosphor-icons/react';
import { differenceInSeconds } from 'date-fns';
import { useRef, useState, useEffect } from 'react';
import { CodeBlock } from 'react-code-blocks';
import { useNavigate } from 'react-router-dom';
import { useNotification } from 'src/contexts/NotificationContext';
import { usePlayRecording } from 'src/contexts/PlayRecordingContext';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import { Agent } from 'src/interfaces/agent.interface';
import { Call, PostCallResults } from 'src/interfaces/call.interface';
import { cn } from 'src/utils/className';
import { copyToClipboard } from 'src/utils/copyToClipboard';
import { friendlyDateFormatter } from 'src/utils/date';
import { processPhoneNumber } from 'src/utils/number';
import { formatSnakeCase } from 'src/utils/string';
import { Button } from './Button';
import {
  CallRecordingButton,
  CallRecordingButtonRef,
} from './CallRecordingButton';
import { CopyableField } from './CopyableField';
import { Modal } from './Modal';
import { CallErrorDisplay } from './CallErrorDisplay';
import { BetaTag } from './BetaTag';
import { Tooltip } from './Tooltip';

type CallDetailsModalProps = {
  call: Call;
  isOpen: boolean;
  onClose: () => void;
};

type TranscriptMessage = {
  timestamp: string;
  speaker: 'HUMAN' | 'BOT';
  message: string;
};

const excludedFields = [
  'transcript',
  'telephony_metadata',
  'user_id',
  'action_history',
  'recording_available',
];

const fieldLabels: Record<string, string> = {
  id: 'ID',
  status: 'Raw Status',
  human_detection_result: 'Human Answered',
  recording_available: 'Recording',
  from_number: 'From Number',
  to_number: 'To Number',
  agent: 'Agent',
  telephony_provider: 'Provider',
  start_time: 'Start Time',
  end_time: 'End Time',
  context: 'Context',
};

const parseTranscript = (transcript: string) => {
  if (!transcript) return [];

  const lines = transcript.split('\n').filter((line) => line.trim());
  const messages: Array<TranscriptMessage> = [];

  lines.forEach((line) => {
    const match = line.match(/^\[([^\]]+)\]\s*(HUMAN|BOT):\s*(.+)$/);
    if (match) {
      messages.push({
        timestamp: match[1],
        speaker: match[2] as 'HUMAN' | 'BOT',
        message: match[3].trim(),
      });
    }
  });

  return messages;
};

export const CallDetailsModal = ({
  call,
  isOpen,
  onClose,
}: CallDetailsModalProps) => {
  const { get } = useAuthedRequest();
  const navigate = useNavigate();
  const notification = useNotification();
  const { triggerPlay, audioUrl, audioRef, cleanupAudio } = usePlayRecording();
  const callRecordingButtonRef = useRef<CallRecordingButtonRef>(null);

  const [currentPlayerTime, setCurrentPlayerTime] = useState(0);
  const [agent, setAgent] = useState<Agent | undefined>(undefined);

  useEffect(() => {
    const fetchAgent = async () => {
      const agent = await get(`/agents/${call.agent}`);
      setAgent(agent);
    };
    fetchAgent();
  }, [call.agent, get]);

  const startDate = call.start_time
    ? new Date(Number(call.start_time) * 1000)
    : null;
  const endDate = call.end_time ? new Date(Number(call.end_time) * 1000) : null;
  const durationSecs =
    (startDate && endDate && differenceInSeconds(endDate, startDate)) || 0;

  const downloadTranscript = () => {
    if (!call.transcript) return;

    const element = document.createElement('a');
    const file = new Blob([call.transcript], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = 'transcript_' + call.id + '.txt';
    document.body.appendChild(element);
    element.click();
  };

  const formatFieldValue = (key: string, value: unknown): React.ReactNode => {
    if (value === null || value === undefined || value === '') return '-';
    if (Array.isArray(value) && value.length === 0) return '-';
    if (
      typeof value === 'object' &&
      !Array.isArray(value) &&
      Object.keys((value as Record<string, unknown>) || {}).length === 0
    ) {
      return '-';
    }

    if (key === 'start_time') {
      return friendlyDateFormatter(startDate);
    }

    if (key === 'end_time') {
      return friendlyDateFormatter(endDate);
    }

    if (key === 'from_number' || key === 'to_number') {
      return processPhoneNumber(value?.toString() || '');
    }

    if (key === 'human_detection_result') {
      if (durationSecs === 0) {
        return '-';
      } else if (value === 'no_human') {
        return (
          <span className="flex items-center gap-2">
            <XCircle
              size={20}
              weight="fill"
              className="flex-shrink-0 text-error"
            />
            <span style={{ transform: 'translateY(2px)' }}>No</span>
          </span>
        );
      } else {
        return (
          <span className="flex items-center gap-2 ">
            <CheckCircle
              size={20}
              weight="fill"
              className="flex-shrink-0 text-success"
            />
            <span style={{ transform: 'translateY(2px)' }}>Yes</span>
          </span>
        );
      }
    }

    if (key === 'recording_available') {
      return value ? 'Available' : 'Not Available';
    }

    if (key === 'post_call_results') {
      return value ? (
        <div className="max-h-40 overflow-y-auto">
          <p>{(value as PostCallResults).content}</p>
        </div>
      ) : null;
    }

    if (key === 'id' || key === 'agent') {
      return (
        <CopyableField
          value={value?.toString() || '-'}
          notification={notification}
        />
      );
    }

    if (key === 'telephony_id') {
      return (
        <CopyableField
          value={call.telephony_metadata?.call_sid || call.telephony_id || '-'}
          notification={notification}
        />
      );
    }

    if (key === 'errors') {
      return (
        <div className="max-h-40 overflow-y-auto">
          <CallErrorDisplay errors={value as string[]} showWarning={false} />
        </div>
      );
    }

    if (typeof value === 'object') {
      return (
        <div className="relative">
          <button
            type="button"
            className="absolute top-2 right-2 z-10 border-none m-0 p-0"
            onClick={() => {
              copyToClipboard(JSON.stringify(value, null, 2), notification);
            }}
            aria-label="Copy JSON"
          >
            <Copy
              className="cursor-pointer w-5 h-5 text-neutral-700"
              size={16}
            />
          </button>

          <CodeBlock
            text={JSON.stringify(value, null, 2)}
            language="json"
            wrapLongLines={false}
            showLineNumbers={false}
          />
        </div>
      );
    }

    return value?.toString() || '-';
  };

  const getTelephonyIdRow = () => {
    if (!call.telephony_provider) return null;

    const label = `${formatSnakeCase(call.telephony_provider)} ID`;
    return (
      <tr key="telephony_id">
        <td className="py-2 px-4 border-b border-neutral-100 font-medium w-[20%]">
          {label}
        </td>
        <td
          className="py-2 px-4 border-b border-neutral-100 break-all text-ellipsis whitespace-nowrap overflow-hidden"
          colSpan={3}
        >
          <CopyableField
            value={
              call.telephony_metadata?.call_sid || call.telephony_id || '-'
            }
            notification={notification}
          />
        </td>
      </tr>
    );
  };

  const orderedFields = [
    'id',
    'status',
    'start_time',
    'end_time',
    'human_detection_result',
    'recording_available',
    'from_number',
    'to_number',
    'agent',
    'telephony_provider',
    'post_call_results',
    'context',
    'avg_latency',
    'max_latency',
  ];

  const remainingFields = call
    ? Object.keys(call).filter(
        (key) =>
          !orderedFields.includes(key) &&
          !excludedFields.includes(key) &&
          call[key as keyof Call] !== null &&
          call[key as keyof Call] !== undefined &&
          call[key as keyof Call] !== '',
      )
    : [];

  const displayFields = [...orderedFields, ...remainingFields];
  const transcriptMessages = parseTranscript(call.transcript || '');

  const parseTimestampToSeconds = (timestamp: string): number => {
    const parts = timestamp.split(':').map((p) => p.trim());
    let hours = 0;
    let minutes = 0;
    let seconds = 0;

    if (parts.length === 3) {
      hours = Number(parts[0]);
      minutes = Number(parts[1]);
      seconds = Number(parts[2]);
    } else if (parts.length === 2) {
      minutes = Number(parts[0]);
      seconds = Number(parts[1]);
    } else if (parts.length === 1) {
      seconds = Number(parts[0]);
    }

    hours = isNaN(hours) ? 0 : hours;
    minutes = isNaN(minutes) ? 0 : minutes;
    seconds = isNaN(seconds) ? 0 : seconds;

    return hours * 3600 + minutes * 60 + seconds;
  };

  const handleMessageClick = async (messageIndex: number) => {
    let targetTime = 0;

    if (messageIndex === 0) {
      targetTime = 0;
    } else {
      const prevMessage = transcriptMessages[messageIndex - 1];

      if (prevMessage) {
        targetTime = parseTimestampToSeconds(prevMessage.timestamp);
      }
    }

    triggerPlay(targetTime);

    if (!audioUrl) {
      callRecordingButtonRef.current?.click();
    }
  };

  const handleClose = () => {
    onClose();
    audioRef.current?.pause();
    cleanupAudio();
  };

  const timestampToSeconds = (timestamp: string) => {
    const parts = timestamp
      .split(':')
      .map((p) => p.trim())
      .reverse();
    const seconds = Number(parts[0]) || 0;
    const minutes = Number(parts[1]) || 0;
    const hours = Number(parts[2]) || 0;
    return hours * 3600 + minutes * 60 + seconds;
  };

  const messageTimeRanges = (messages: TranscriptMessage[]) => {
    return messages.map((msg, index) => {
      const endTime = timestampToSeconds(msg.timestamp);
      const startTime =
        index === 0 ? 0 : timestampToSeconds(messages[index - 1].timestamp);

      return {
        ...msg,
        startTime,
        endTime,
      };
    });
  };

  const getCurrentMessageIndex = (
    currentTime: number,
    messages: TranscriptMessage[],
  ) => {
    const messagesWithRanges = messageTimeRanges(messages);

    const index = messagesWithRanges.findIndex(
      ({ startTime, endTime }) =>
        currentTime >= startTime && currentTime < endTime,
    );

    return index !== -1 ? index : messages.length - 1;
  };

  useEffect(() => {
    const currentMessageIndex = getCurrentMessageIndex(
      currentPlayerTime,
      transcriptMessages,
    );
    if (currentMessageIndex >= 0) {
      const messageElement = document.getElementById(
        `message-${currentMessageIndex}`,
      );
      if (messageElement) {
        messageElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
    }
  }, [currentPlayerTime, transcriptMessages]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Call Overview"
      className="lg:max-w-[90%] max-w-7xl"
    >
      <div className="flex gap-6 h-[78vh]">
        <div className="flex-1 flex flex-col">
          {call.transcript && (
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Transcript</h3>
              <div className="flex gap-2">
                <Tooltip
                  content="Only available for OpenAI agents with human interaction"
                  zIndex={50}
                  position="bottom"
                >
                  <Button
                    onClick={() =>
                      navigate(`/calls/${call.id}/conversation-editor`)
                    }
                    size="small"
                    color="secondary"
                    className="gap-2"
                    disabled={
                      !call.transcript ||
                      parseTranscript(call.transcript).length <= 1 ||
                      (agent?.provider && agent.provider !== 'openai')
                    }
                  >
                    Edit Conversation
                    <BetaTag size="small" />
                  </Button>
                </Tooltip>
                <Button onClick={downloadTranscript} size="small">
                  Download
                </Button>
              </div>
            </div>
          )}

          {call.transcript && (
            <div className="flex-1 overflow-y-auto border border-neutral-200 rounded-lg p-4 bg-neutral-50">
              {transcriptMessages.length > 0 && (
                <div className="space-y-4">
                  {transcriptMessages.map((msg, index) => {
                    const isHuman = msg.speaker === 'HUMAN';
                    const currentMessageIndex = getCurrentMessageIndex(
                      currentPlayerTime,
                      transcriptMessages,
                    );
                    const isCurrentMessage = index === currentMessageIndex;

                    return (
                      <div
                        key={index}
                        id={`message-${index}`}
                        className={`flex ${isHuman ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          onClick={() => handleMessageClick(index)}
                          className={cn(
                            `max-w-[80%] rounded-lg px-4 py-2 cursor-pointer transition-opacity ${
                              isHuman
                                ? 'bg-primary text-neutral-0 hover:opacity-80'
                                : 'bg-neutral-0 text-neutral-800 border border-neutral-200 hover:opacity-60'
                            }`,
                            isCurrentMessage && currentPlayerTime > 0
                              ? `border-2 shadow-lg border-gold-300`
                              : '',
                          )}
                        >
                          <div className="text-xs opacity-70 mb-1">
                            {msg.timestamp} - {msg.speaker}
                          </div>
                          <div className="whitespace-pre-wrap">
                            {msg.message}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {transcriptMessages.length === 0 && (
                <div className="text-neutral-500 italic">{call.transcript}</div>
              )}
            </div>
          )}

          {!call.transcript && (
            <div className="flex-1 border border-neutral-200 rounded-lg p-4 bg-neutral-50 flex items-center justify-center">
              <p className="text-neutral-400">No transcript available.</p>
            </div>
          )}

          {call.recording_available ? (
            <div className="flex justify-center mt-4">
              <CallRecordingButton
                ref={callRecordingButtonRef}
                callId={call.id}
                recordingAvailable={call.recording_available}
                setCurrentTime={setCurrentPlayerTime}
                showTimeline
              />
            </div>
          ) : (
            <div className="flex justify-center mt-4">
              <p className="text-neutral-400">No recording available.</p>
            </div>
          )}
        </div>

        <div className="flex-1 flex flex-col">
          <h3 className="text-lg font-semibold mb-4">Details</h3>
          <div className="flex-1 overflow-y-auto">
            <table className="w-full border border-neutral-100 bg-neutral-0 table-fixed">
              <tbody>
                {(() => {
                  const validFields = displayFields.filter((key) => {
                    if (excludedFields.includes(key)) return false;

                    const value = call[key as keyof Call];
                    if (value === null || value === undefined || value === '')
                      return false;
                    if (Array.isArray(value) && value.length === 0)
                      return false;
                    if (
                      typeof value === 'object' &&
                      !Array.isArray(value) &&
                      value !== null &&
                      Object.keys(value as Record<string, unknown>).length === 0
                    )
                      return false;

                    return true;
                  });

                  const rows: JSX.Element[] = [];

                  for (let i = 0; i < validFields.length; i += 2) {
                    const firstKey = validFields[i];
                    const secondKey = validFields[i + 1];

                    rows.push(
                      <tr key={firstKey}>
                        <td className="py-2 px-4 border-b border-neutral-100 font-medium w-[20%]">
                          {fieldLabels[firstKey] || formatSnakeCase(firstKey)}
                        </td>
                        <td className="py-2 px-4 border-b border-neutral-100 w-[30%] break-all">
                          {formatFieldValue(
                            firstKey,
                            (call as Record<string, unknown>)[firstKey],
                          )}
                        </td>

                        {secondKey ? (
                          <>
                            <td className="py-2 px-4 border-b border-neutral-100 font-medium w-[20%]">
                              {fieldLabels[secondKey] ||
                                formatSnakeCase(secondKey)}
                            </td>
                            <td className="py-2 px-4 border-b border-neutral-100 w-[30%]">
                              {formatFieldValue(
                                secondKey,
                                (call as Record<string, unknown>)[secondKey],
                              )}
                            </td>
                          </>
                        ) : (
                          <>
                            <td className="py-2 px-4 border-b border-neutral-100 w-[20%]"></td>
                            <td className="py-2 px-4 border-b border-neutral-100 w-[30%]"></td>
                          </>
                        )}
                      </tr>,
                    );
                  }

                  return rows;
                })()}

                {getTelephonyIdRow()}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Modal>
  );
};
